import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Zap, RefreshCw, Info, AlertCircle } from 'lucide-react';
import { addUtilityBill, updateUtilityBill } from './api';
import { useAppTheme } from '../../contexts/ThemeContext';

/**
 * ManageUtilityCharge
 * Props:
 *   tenant      — tenant object (needs .id, .balance)
 *   bill        — null for Add mode, bill object for Edit mode
 *   onSaved()   — called after a successful save (parent refreshes list)
 *   onClose()   — called to dismiss the dialog
 */

const ModalOverlay = ({ children }) =>
  createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 9999 }}
    >
      {children}
    </div>,
    document.body
  );

const ManageUtilityCharge = ({ tenant, bill = null, onSaved, onClose }) => {
  const { palette, Colors } = useAppTheme();
  const bg     = palette.background;
  const txt    = palette.text;
  const border = palette.border;
  const P      = Colors.primary.main;

  const isEdit = Boolean(bill);

  // ── form state ─────────────────────────────────────────────────────────────
  const [chargeType, setChargeType] = useState(bill?.chargeType ?? 'monthly');
  const [type,       setType]       = useState(bill?.type       ?? '');
  const [amount,     setAmount]     = useState(bill?.amount != null ? String(bill.amount) : '');
  const [deductNow,  setDeductNow]  = useState(false);

  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  const inputClass = 'w-full px-4 py-2.5 rounded-lg focus:outline-none focus:ring-2';
  const inputStyle = { backgroundColor: bg.tertiary, border: `1px solid ${border}`, color: txt.primary };

  // ── derived info copy ──────────────────────────────────────────────────────
  const oneTimeInfo = {
    icon: <Zap className="w-4 h-4" />,
    color: Colors.status.warning ?? '#f59e0b',
    title: 'One-time charge',
    body: isEdit
      ? 'The difference from the old amount will be applied to the tenant\'s balance immediately. This charge is not recurring.'
      : 'This charge will be deducted from the tenant\'s balance immediately, once only. It will not recur next month.',
  };

  const monthlyOffInfo = {
    icon: <RefreshCw className="w-4 h-4" />,
    color: P,
    title: 'Monthly recurring — saved only',
    body: 'This charge will be saved and will begin deducting automatically from next month\'s processing. Nothing is deducted today.',
  };

  const monthlyOnInfo = {
    icon: <RefreshCw className="w-4 h-4" />,
    color: Colors.status.success,
    title: 'Monthly recurring — deduct now too',
    body: isEdit
      ? 'The new amount will be deducted immediately today, and will continue to recur every month going forward.'
      : 'This charge will be deducted immediately today, and will also recur every month going forward.',
  };

  const info = chargeType === 'onetime'
    ? oneTimeInfo
    : deductNow
      ? monthlyOnInfo
      : monthlyOffInfo;

  // ── submit ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!type.trim())           { setError('Please enter a charge name');    return; }
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) { setError('Please enter a valid amount');   return; }

    setError(null);
    setSaving(true);

    const payload = {
      type:       type.trim(),
      amount:     amt,
      chargeType,
      ...(chargeType === 'monthly' && { deductNow }),
    };

    try {
        let response;
        if (isEdit) {
          response = await updateUtilityBill(tenant.id, bill.id, payload);
        } else {
          response = await addUtilityBill(tenant.id, payload);
        }
      if (onSaved) onSaved(response);
    } catch (e) {
      setError(typeof e === 'string' ? e : `Failed to ${isEdit ? 'update' : 'add'} charge`);
    } finally {
      setSaving(false);
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <ModalOverlay>
      <div
        className="rounded-2xl w-full max-w-xl p-8 shadow-2xl"
        style={{ backgroundColor: bg.secondary, border: `1px solid ${border}` }}
      >
        {/* Header */}
        <h3 className="text-2xl font-bold mb-1" style={{ color: txt.primary }}>
          {isEdit ? `Edit — ${bill.type}` : 'Add New Charge'}
        </h3>
        <p className="text-sm mb-6" style={{ color: txt.muted }}>
          {isEdit ? 'Update the charge details below.' : 'Configure the charge type first — options will adapt.'}
        </p>

        {/* Error */}
        {error && (
          <div
            className="flex items-center gap-2 p-3 rounded-lg text-sm mb-5"
            style={{
              backgroundColor: `${Colors.status.error}15`,
              border: `1px solid ${Colors.status.error}40`,
              color: Colors.status.error,
            }}
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* ── Step 1: Charge frequency ───────────────────────────────────── */}
        <div className="mb-5">
          <label className="block text-sm font-semibold mb-2" style={{ color: txt.secondary }}>
            1. Charge Frequency
          </label>
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: 'monthly', label: 'Monthly Recurring', icon: <RefreshCw className="w-4 h-4" /> },
              { value: 'onetime', label: 'One-time Charge',   icon: <Zap        className="w-4 h-4" /> },
            ].map(opt => {
              const active = chargeType === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => { setChargeType(opt.value); setDeductNow(false); }}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl font-medium text-sm transition-all"
                  style={{
                    backgroundColor: active ? `${P}18` : bg.tertiary,
                    border: `2px solid ${active ? P : border}`,
                    color: active ? P : txt.secondary,
                  }}
                >
                  {opt.icon}
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Deduct Now toggle (monthly only) ──────────────────────────── */}
        {chargeType === 'monthly' && (
          <div
            className="flex items-center justify-between px-4 py-3 rounded-xl mb-5"
            style={{
              backgroundColor: deductNow ? `${Colors.status.success}12` : bg.tertiary,
              border: `1px solid ${deductNow ? `${Colors.status.success}40` : border}`,
            }}
          >
            <div>
              <p className="text-sm font-medium" style={{ color: txt.primary }}>
                Deduct this month too
              </p>
              <p className="text-xs mt-0.5" style={{ color: txt.muted }}>
                {deductNow
                  ? 'Will apply immediately + recur monthly'
                  : 'Off — will only save and start from next month'}
              </p>
            </div>
            {/* Toggle switch */}
            <button
              onClick={() => setDeductNow(v => !v)}
              className="relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-200"
              style={{ backgroundColor: deductNow ? Colors.status.success : border }}
            >
              <span
                className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200"
                style={{ transform: deductNow ? 'translateX(20px)' : 'translateX(0)' }}
              />
            </button>
          </div>
        )}

        {/* ── Info panel — adapts to selection ──────────────────────────── */}
        <div
          className="flex gap-3 p-4 rounded-xl mb-6"
          style={{
            backgroundColor: `${info.color}12`,
            border: `1px solid ${info.color}35`,
          }}
        >
          <div
            className="flex-shrink-0 mt-0.5 flex items-center justify-center w-7 h-7 rounded-full"
            style={{ backgroundColor: `${info.color}25`, color: info.color }}
          >
            {info.icon}
          </div>
          <div>
            <p className="text-sm font-semibold mb-1" style={{ color: info.color }}>
              {info.title}
            </p>
            <p className="text-xs leading-relaxed" style={{ color: txt.muted }}>
              {info.body}
            </p>
          </div>
        </div>

        {/* ── Step 2: Charge details ─────────────────────────────────────── */}
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2" style={{ color: txt.secondary }}>
            2. Charge Name <span style={{ color: Colors.status.error }}>*</span>
          </label>
          <input
            type="text"
            value={type}
            onChange={e => setType(e.target.value)}
            placeholder="e.g. WiFi, Water, Garbage, Cleaning…"
            autoFocus={!isEdit}
            className={inputClass}
            style={inputStyle}
          />
        </div>

        <div className="mb-7">
          <label className="block text-sm font-semibold mb-2" style={{ color: txt.secondary }}>
            3. Amount (KES)
          </label>
          <input
            type="number"
            min="0"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="0"
            className={inputClass}
            style={inputStyle}
          />
          {amount && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0 && (
            <p className="text-xs mt-1.5 font-medium" style={{
              color: chargeType === 'onetime' ? (Colors.status.warning ?? '#f59e0b') : P,
            }}>
              {parseFloat(amount).toLocaleString('en-KE', { minimumFractionDigits: 2 })} KES
              {chargeType === 'onetime'
                ? ' — will be deducted from the tenant\'s balance immediately.'
                : deductNow
                  ? ' — will be deducted immediately and recur monthly.'
                  : ' — will be saved and deducted from next month.'}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-5 py-2.5 rounded-lg font-medium"
            style={{ backgroundColor: bg.tertiary, color: txt.primary, border: `1px solid ${border}` }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2.5 text-white rounded-lg font-medium"
            style={{ backgroundColor: P }}
          >
            {saving
              ? (isEdit ? 'Updating…' : 'Adding…')
              : (isEdit ? 'Update Charge' : 'Add Charge')}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
};

export default ManageUtilityCharge;